import { useState } from "react";
import { Mail, Phone, MapPin, CheckCircle, Send } from "lucide-react";
import { ACCENT, ACCENT_LIGHT } from "../constants/theme";
import Btn from "../components/Button";
import SectionLabel from "../components/SectionLabel";
import Input from "../components/Input";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};

    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/))
      e.email = "Valid email required";
    if (!form.message.trim()) e.message = "Message is required";

    return e;
  };

  const submit = (event) => {
    event.preventDefault();

    const e = validate();

    if (Object.keys(e).length) {
      setErrors(e);
      setSuccess(false);
      return;
    }

    setSuccess(true);
    setForm({ name: "", email: "", phone: "", message: "" });
    setErrors({});
  };

  return (
    <div>
      <section
        style={{
          background: ACCENT_LIGHT,
          padding: "72px 24px 56px",
          textAlign: "center",
        }}
      >
        <SectionLabel>Get in Touch</SectionLabel>

        <h1
          style={{
            fontSize: "clamp(30px, 5vw, 52px)",
            fontWeight: 900,
            color: "#0f172a",
            letterSpacing: "-1px",
            marginBottom: 14,
          }}
        >
          Contact Us
        </h1>

        <p
          style={{
            color: "#475569",
            fontSize: 16,
            maxWidth: 480,
            margin: "0 auto",
          }}
        >
          Have a question, a project idea, or just want to say hello? We'd love
          to hear from you.
        </p>
      </section>

      <section style={{ background: "#fff", padding: "72px 24px" }}>
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 48,
          }}
        >
          <form onSubmit={submit} noValidate>
            <h2
              style={{
                fontWeight: 800,
                fontSize: 24,
                color: "#0f172a",
                marginBottom: 28,
              }}
            >
              Send a Message
            </h2>

            {success && (
              <div
                role="status"
                aria-live="polite"
                style={{
                  background: "#f0fdf4",
                  border: "1px solid #86efac",
                  borderRadius: 10,
                  padding: "16px 20px",
                  marginBottom: 24,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <CheckCircle size={20} color="#16a34a" aria-hidden="true" />
                <p style={{ color: "#15803d", fontWeight: 600, fontSize: 14 }}>
                  Message sent! We'll get back to you soon.
                </p>
              </div>
            )}

            {["name", "email", "phone"].map((f) => (
              <div key={f}>
                <Input
                  label={
                    f === "name"
                      ? "Your Name"
                      : f === "email"
                      ? "Email Address"
                      : "Phone Number"
                  }
                  type={f === "email" ? "email" : f === "phone" ? "tel" : "text"}
                  value={form[f]}
                  onChange={(e) => {
                    setForm((p) => ({ ...p, [f]: e.target.value }));
                    setErrors((p) => ({ ...p, [f]: "" }));
                  }}
                  required={f !== "phone"}
                  placeholder={
                    f === "name"
                      ? "Jane Doe"
                      : f === "email"
                      ? "jane@example.com"
                      : "+91 98765 43210"
                  }
                />

                {errors[f] && (
                  <p style={{ color: "#ef4444", fontSize: 12.5, marginTop: -14, marginBottom: 10 }}>
                    {errors[f]}
                  </p>
                )}
              </div>
            ))}

            <Input
              label="Message"
              as="textarea"
              rows={5}
              value={form.message}
              onChange={(e) => {
                setForm((p) => ({ ...p, message: e.target.value }));
                setErrors((p) => ({ ...p, message: "" }));
              }}
              required
              placeholder="Tell us what's on your mind..."
            />

            {errors.message && (
              <p style={{ color: "#ef4444", fontSize: 12.5, marginTop: -14, marginBottom: 10 }}>
                {errors.message}
              </p>
            )}

            <Btn type="submit" style={{ width: "100%", justifyContent: "center" }}>
              Send Message <Send size={16} aria-hidden="true" />
            </Btn>
          </form>

          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <h2
              style={{
                fontWeight: 800,
                fontSize: 24,
                color: "#0f172a",
                marginBottom: 8,
              }}
            >
              Studio Info
            </h2>

            {[
              {
                icon: <Mail size={22} color={ACCENT} aria-hidden="true" />,
                title: "Email Us",
                text: "hello@nexusstudio.in",
                sub: "We reply within 24 hours",
                link: "mailto:hello@nexusstudio.in",
              },
              {
                icon: <Phone size={22} color={ACCENT} aria-hidden="true" />,
                title: "Call Us",
                text: "+91 83205 57290",
                sub: "Mon – Fri, 10am – 6pm IST",
                link: "tel:+918320557290",
              },
              {
                icon: <MapPin size={22} color={ACCENT} aria-hidden="true" />,
                title: "Visit Us",
                text: "Nexus Studio, Ahmedabad",
                sub: "Gujarat, India 380001",
              },
            ].map(({ icon, title, text, sub, link }) => (
              <div
                key={title}
                style={{
                  display: "flex",
                  gap: 16,
                  padding: 24,
                  borderRadius: 14,
                  border: "1px solid #e2e8f0",
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    background: ACCENT_LIGHT,
                    borderRadius: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {icon}
                </div>

                <div>
                  <p style={{ fontWeight: 700, fontSize: 15, color: "#0f172a", marginBottom: 4 }}>
                    {title}
                  </p>

                  {link ? (
                    <a
                      href={link}
                      style={{
                        color: "#374151",
                        fontSize: 14.5,
                        fontWeight: 500,
                        textDecoration: "none",
                      }}
                    >
                      {text}
                    </a>
                  ) : (
                    <p style={{ color: "#374151", fontSize: 14.5, fontWeight: 500 }}>
                      {text}
                    </p>
                  )}

                  <p style={{ color: "#94a3b8", fontSize: 12.5 }}>{sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}